import React, { ReactElement } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { COLORS, FONT } from './tokens';
import { MaterialSymbol } from './MaterialSymbol';
import { YellowCtaButton } from './YellowCtaButton';

export interface FilterChipDescriptor {
  id: string;

  label: string;

  onRemove: () => void;
}

export interface NoResultsStateProps {
  icon?: string;
  title: string;
  body: string;

  chips?: FilterChipDescriptor[];

  clearLabel?: string;
  onClear?: () => void;
}

export interface ErrorStateProps {
  title?: string;
  body?: string;
  retryLabel?: string;
  onRetry?: () => void;
}

const Card = ({ children }: { children: React.ReactNode }): ReactElement => (
  <Box
    sx={{
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.cardBorder}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'center',
      px: '32px',
      py: '48px',
      width: '100%',
    }}
  >
    {children}
  </Box>
);

const RemovableChip = ({
  label,
  onRemove,
}: FilterChipDescriptor): ReactElement => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: '32px',
      pl: '12px',
      pr: '8px',
      borderRadius: '9999px',
      backgroundColor: COLORS.scopeTrackBg,
      border: `1px solid ${COLORS.filterBorder}`,
    }}
  >
    <Typography
      component="span"
      sx={{
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: '13px',
        color: COLORS.navy,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <ButtonBase
      onClick={onRemove}
      disableRipple
      aria-label={`Remove ${label}`}
      sx={{
        borderRadius: '50%',
        p: '2px',
        color: COLORS.navy,
        '&:hover': { backgroundColor: 'rgba(35,48,95,0.12)' },
        '&:focus-visible': {
          outline: `2px solid ${COLORS.navy}`,
          outlineOffset: '1px',
        },
      }}
    >
      <MaterialSymbol name="close" size={16} color={COLORS.navy} />
    </ButtonBase>
  </Box>
);

export const NoResultsState = ({
  icon = 'search_off',
  title,
  body,
  chips = [],
  clearLabel,
  onClear,
}: NoResultsStateProps): ReactElement => (
  <Card>
    <Box
      sx={{
        width: '84px',
        height: '84px',
        borderRadius: '50%',
        backgroundColor: COLORS.scopeTrackBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <MaterialSymbol name={icon} size={36} color={COLORS.muted} />
    </Box>
    <Typography
      sx={{
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: '16px',
        color: COLORS.headingDark,
        lineHeight: 'normal',
        textAlign: 'center',
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT,
        fontWeight: 400,
        fontSize: '14px',
        color: COLORS.muted,
        lineHeight: '20px',
        textAlign: 'center',
        maxWidth: '420px',
      }}
    >
      {body}
    </Typography>
    {chips.length > 0 && (
      <Box
        sx={{
          mt: '4px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          justifyContent: 'center',
        }}
      >
        {chips.map(chip => (
          <RemovableChip key={chip.id} {...chip} />
        ))}
      </Box>
    )}
    {clearLabel && onClear && (
      <ButtonBase
        onClick={onClear}
        disableRipple
        sx={{
          mt: '8px',
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: '14px',
          color: COLORS.navy,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
          '&:hover': { color: COLORS.headingDark },
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: '2px',
          },
        }}
      >
        {clearLabel}
      </ButtonBase>
    )}
  </Card>
);

export const ErrorState = ({
  title = 'Something went sideways.',
  body = 'We couldn’t load this right now. Check your connection and try again.',
  retryLabel = 'Try again',
  onRetry,
}: ErrorStateProps): ReactElement => (
  <Card>
    <MaterialSymbol name="cloud_off" size={32} color={COLORS.muted} />
    <Typography
      sx={{
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: '16px',
        color: COLORS.headingDark,
        lineHeight: 'normal',
        textAlign: 'center',
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONT,
        fontWeight: 400,
        fontSize: '14px',
        color: COLORS.muted,
        lineHeight: '20px',
        textAlign: 'center',
        maxWidth: '420px',
      }}
    >
      {body}
    </Typography>
    {onRetry && (
      <Box mt="4px">
        <YellowCtaButton onClick={onRetry} height={45}>
          {retryLabel}
        </YellowCtaButton>
      </Box>
    )}
  </Card>
);
